import { Component, OnDestroy, AfterViewInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { PaymentGatewayService } from '../../services/payment-gateway.service';
import { PaymentContext, VaultedPaymentMethod } from '../../interfaces/payment-gateway.interface';

type PaymentMode = 'saved' | 'new';

@Component({
  selector: 'app-braintree-checkout',
  templateUrl: './braintree-checkout.component.html',
  styleUrls: ['./braintree-checkout.component.scss']
})
export class BraintreeCheckoutComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() context: PaymentContext = PaymentContext.ADHOC_INVOICE;
  @Input() threeDSecure = false;
  @Input() amount       = 0;
  @Input() vaultOnSuccess = false; // Whether to vault new card on successful payment
  @Input() reloadToken = 0;

  @Output() paymentNonce = new EventEmitter<string>();
  @Output() paymentError = new EventEmitter<string>();
  @Output() cardDeleted  = new EventEmitter<string>();

  isLoading        = true;
  errorMessage     = '';
  isSubmitting     = false;
  isDeletingCard   = false;

  // Vaulted payment methods
  vaultedMethods: VaultedPaymentMethod[] = [];
  selectedVaultedNonce: string | null    = null;

  // Payment mode: 'saved' (use vaulted card) or 'new' (enter new card)
  paymentMode: PaymentMode = 'new';

  // Hosted fields state
  hostedFieldsReady = false;
  fieldValidity = {
    number: false,
    cvv: false,
    expirationDate: false
  };
  private initChain: Promise<void> = Promise.resolve();
  private destroyed = false;

  constructor(private paymentGatewayService: PaymentGatewayService) {}

  async ngAfterViewInit(): Promise<void> {
    await this.enqueueInit(() => this.initializeCheckout());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadToken'] && !changes['reloadToken'].firstChange) {
      void this.reloadBraintree();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    void this.paymentGatewayService.resetBraintree();
  }

  private async reloadBraintree(): Promise<void> {
    await this.enqueueInit(async () => {
      await this.paymentGatewayService.resetBraintree();
      await this.initializeCheckout();
    });
  }

  private enqueueInit(task: () => Promise<void>): Promise<void> {
    this.initChain = this.initChain.then(async () => {
      if (this.destroyed) return;
      await task();
    }).catch(() => {});
    return this.initChain;
  }

  private async initializeCheckout(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    await this.paymentGatewayService.resetBraintree();
    this.isLoading = true;
    this.errorMessage = '';
    this.hostedFieldsReady = false;
    this.fieldValidity = {
      number: false,
      cvv: false,
      expirationDate: false
    };
    this.paymentMode = 'new';
    this.vaultedMethods = [];
    this.selectedVaultedNonce = null;

    // Small delay to ensure DOM is fully rendered
    await new Promise(resolve => setTimeout(resolve, 0));
    
    try {
      // Initialize Braintree client first
      const initialized = await this.paymentGatewayService.initializeBraintree();
      if (!initialized) {
        throw new Error('Failed to initialize Braintree');
      }

      // Initialize hosted fields for new card entry
      await this.initializeHostedFields();
    } catch (err: any) {
      this.errorMessage = err?.message || 'Payment form initialization failed';
      this.paymentError.emit(this.errorMessage);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadVaultedMethods(): Promise<void> {
    this.vaultedMethods = await this.paymentGatewayService.fetchVaultedPaymentMethods();
    
    // If customer has saved cards, default to saved mode with first card selected
    if (this.vaultedMethods.length > 0) {
      this.paymentMode = 'saved';
      // Select the default card, or first card if no default
      const defaultMethod = this.vaultedMethods.find(m => m.default) || this.vaultedMethods[0];
      this.selectedVaultedNonce = defaultMethod.nonce;
    }
  }

  private async initializeHostedFields(): Promise<void> {
    const hostedFields = await this.paymentGatewayService.createBraintreeHostedFields({
      styles: {
        input: {
          'font-size': '16px',
          'font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          color: '#333'
        },
        ':focus': {
          color: '#333'
        },
        '.valid': {
          color: '#28a745'
        },
        '.invalid': {
          color: '#dc3545'
        }
      },
      fields: {
        number:         { selector: '#bt-card-number',     placeholder: '4111 1111 1111 1111' },
        cvv:            { selector: '#bt-cvv',             placeholder: '123' },
        expirationDate: { selector: '#bt-expiration-date', placeholder: 'MM/YY' }
      }
    });

    // Listen for field validity changes
    hostedFields.on('validityChange', (event: any) => {
      const field = event.fields[event.emittedBy];
      (this.fieldValidity as any)[event.emittedBy] = field.isValid;
    });

    this.hostedFieldsReady = true;
  }

  selectPaymentMode(mode: PaymentMode): void {
    this.paymentMode = mode;
    this.errorMessage = '';
    
    if (mode === 'saved' && this.vaultedMethods.length > 0) {
      // Select first saved card if none selected
      if (!this.selectedVaultedNonce) {
        this.selectedVaultedNonce = this.vaultedMethods[0].nonce;
      }
    }
  }

  selectVaultedCard(nonce: string): void {
    this.selectedVaultedNonce = nonce;
    this.errorMessage = '';
  }

  async deleteVaultedCard(nonce: string, event: Event): Promise<void> {
    event.stopPropagation();
    
    if (this.isDeletingCard) return;
    this.isDeletingCard = true;

    try {
      const success = await this.paymentGatewayService.deleteVaultedPaymentMethod(nonce);
      if (success) {
        this.vaultedMethods = this.vaultedMethods.filter(m => m.nonce !== nonce);
        this.cardDeleted.emit(nonce);
        
        // If deleted card was selected, select another or switch to new mode
        if (this.selectedVaultedNonce === nonce) {
          if (this.vaultedMethods.length > 0) {
            this.selectedVaultedNonce = this.vaultedMethods[0].nonce;
          } else {
            this.selectedVaultedNonce = null;
            this.paymentMode = 'new';
          }
        }
      } else {
        this.errorMessage = 'Failed to delete saved card';
      }
    } catch (err: any) {
      this.errorMessage = err?.message || 'Failed to delete saved card';
    } finally {
      this.isDeletingCard = false;
    }
  }

  isNewCardFormValid(): boolean {
    return this.fieldValidity.number && 
           this.fieldValidity.cvv && 
           this.fieldValidity.expirationDate;
  }

  canSubmit(): boolean {
    if (this.paymentMode === 'saved') {
      return !!this.selectedVaultedNonce;
    }
    return this.hostedFieldsReady && this.isNewCardFormValid();
  }

  async submitPayment(): Promise<void> {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.errorMessage = '';

    try {
      let nonce: string | null = null;

      if (this.paymentMode === 'saved' && this.selectedVaultedNonce) {
        // Use the vaulted card nonce directly
        nonce = this.selectedVaultedNonce;
      } else {
        // Tokenize new card using hosted fields
        nonce = await this.paymentGatewayService.createBraintreePaymentNonce();
      }

      if (!nonce) {
        throw new Error('Failed to generate payment nonce. Please check your card details.');
      }

      this.paymentNonce.emit(nonce);
    } catch (err: any) {
      this.errorMessage = err?.message || 'Payment submission failed';
      this.paymentError.emit(this.errorMessage);
    } finally {
      this.isSubmitting = false;
    }
  }

  getCardIcon(cardType?: string): string {
    const icons: Record<string, string> = {
      'Visa':             'fab fa-cc-visa',
      'MasterCard':       'fab fa-cc-mastercard',
      'Mastercard':       'fab fa-cc-mastercard',
      'American Express': 'fab fa-cc-amex',
      'Amex':             'fab fa-cc-amex',
      'Discover':         'fab fa-cc-discover',
      'JCB':              'fab fa-cc-jcb',
      'Diners Club':      'fab fa-cc-diners-club',
      'PayPal':           'fab fa-cc-paypal'
    };
    return icons[cardType || ''] || 'fas fa-credit-card';
  }
}
